import React from 'react'
import { TextField, InputAdornment, IconButton } from '@mui/material'
import { Clear } from '@mui/icons-material'

interface OptionalNumberFieldProps {
  /** Form field name */
  name: string
  /** Display label */
  label: string
  /** Current value (undefined when not set) */
  value: number | undefined
  /** Unique ID for the input */
  id: string
  /** Handler for input changes */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Handler for clearing the field */
  onClear: () => void
  /** Minimum allowed value */
  min?: number
  /** Maximum allowed value */
  max?: number
  /** Step increment */
  step?: number
}

/**
 * A number input field for optional values.
 * Shows a clear button when a value is set, allowing users to reset to undefined.
 */
export const OptionalNumberField: React.FC<OptionalNumberFieldProps> = ({
  name,
  label,
  value,
  id,
  onChange,
  onClear,
  min,
  max,
  step,
}) => {
  return (
    <TextField
      type="number"
      slotProps={{ 
        htmlInput: { 
          min, 
          max, 
          step,
          'aria-label': label,
        },
        input: {
          endAdornment: value !== undefined && (
            <InputAdornment position="end">
              <IconButton
                aria-label={`clear ${label.toLowerCase()}`}
                onClick={onClear}
                edge="end"
                size="small"
              >
                <Clear />
              </IconButton>
            </InputAdornment>
          )
        }
      }}
      id={id}
      name={name}
      label={label}
      value={value ?? ''}
      onChange={onChange}
      fullWidth
    />
  )
}

export default OptionalNumberField
